import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, of, switchMap, tap, throwError } from 'rxjs';
import { ALLOWED_ROLES, API_BASE_URL } from '../config/api.config';
import { ActionResponse, AuthResponse, LoginRequest } from '../models/auth.models';
import { UserProfile } from '../models/user.models';
import { TokenStorageService } from './token-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenStore = inject(TokenStorageService);

  private readonly userSubject = new BehaviorSubject<UserProfile | null>(null);
  readonly user$ = this.userSubject.asObservable();

  constructor() {
    if (this.isAuthenticated()) {
      this.refreshProfile().subscribe({
        error: () => undefined,
      });
    }
  }

  login(payload: LoginRequest): Observable<UserProfile> {
    return this.http.post<AuthResponse>(`${API_BASE_URL}/auth/login`, payload).pipe(
      tap((response) => {
        if (!response.accessToken) {
          throw new Error(response.message ?? 'Connexion impossible.');
        }
        this.tokenStore.saveToken(response.accessToken);
      }),
      switchMap(() => this.http.get<UserProfile>(`${API_BASE_URL}/users/me`)),
      tap((user) => this.userSubject.next(user)),
      catchError((error) => {
        this.tokenStore.clear();
        this.userSubject.next(null);
        return throwError(() => error);
      })
    );
  }

  requestPasswordReset(email: string): Observable<ActionResponse> {
    return this.http.post<ActionResponse>(`${API_BASE_URL}/auth/password/forgot`, { email });
  }

  refreshProfile(): Observable<UserProfile | null> {
    if (!this.isAuthenticated()) {
      this.userSubject.next(null);
      return of(null);
    }

    return this.http.get<UserProfile>(`${API_BASE_URL}/users/me`).pipe(
      tap((user) => this.userSubject.next(user)),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
          this.logout();
          return of(null);
        }
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    this.tokenStore.clear();
    this.userSubject.next(null);
  }

  isAuthenticated(): boolean {
    return !!this.tokenStore.getToken();
  }

  getToken(): string | null {
    return this.tokenStore.getToken();
  }

  get currentUser(): UserProfile | null {
    return this.userSubject.value;
  }

  hasAdminAccess(user: UserProfile | null): boolean {
    return !!user?.role && ALLOWED_ROLES.includes(user.role);
  }

  isAdmin(user: UserProfile | null): boolean {
    return user?.role === 'ADMIN';
  }
}
